import FormGeneralError from 'components/formComponents/FormGeneralError';
import React, { useState } from 'react';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { MyDocumentsApi } from '../../api/myDocumentsApi';
import { useDispatch } from 'react-redux';
import { setUpdateMyDocumentsList } from 'reducers/misc';
import instance, { ApiResponse, useFetch } from 'api';
import { endpoints } from 'endpoints.config';
import warningIcon from '../../images/Icon_Warning_01.png';
import { Spinner } from 'components/spinner/Spinner';

type FailedDeletion = {
  id: number;
  reason: string;
};

type DeletionResponse = {
  successfulDeletions: number[];
  failedDeletions: FailedDeletion[];
};

type ErrorDeletion = {
  hasError: boolean;
  failedDeletions: FailedDeletion[];
};
type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  addPluralTxt: boolean | undefined;
  ids: number[];
  selectedData: any[];
};

export type GetDeleteFoldersResponse = {
  details: {
    documentsToDelete: number[];
    foldersAndDocumentsToDelete: {
      folderAndDocument: string;
    }[];
    foldersToDelete: number[];
  };
};

export const DeleteFolders: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  addPluralTxt,
  ids,
  selectedData,
}) => {
  const dispatch = useDispatch();
  const { loading, data, error } = useFetch<GetDeleteFoldersResponse>(
    endpoints.myDocuments.deleteFolders,
    {
      paramsSerializer: (params) => {
        return ids.map((id) => `foldersIds=${id}`).join('&');
      },
      params: { foldersIds: ids },
    }
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorState, setErrorState] = useState<ErrorDeletion | undefined>();

  const deleteFolder = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    const payload = { idsToDelete: ids };
    instance
      .post<ApiResponse<any, DeletionResponse>>(
        endpoints.myDocuments.deleteFolders,
        payload
      )
      .then((res) => {
        if (res.status === 200) {
          if (
            res.data.status === '1' &&
            res.data.details.failedDeletions.length === 0
          ) {
            dispatch(setUpdateMyDocumentsList(true));
            togglePanelOpen(false);
          } else {
            // Failed deleting some users
            setErrorState({
              hasError: true,
              failedDeletions: res.data.details.failedDeletions,
            });
          }
        }
      })
      .catch((err) => {
        // Generic error
        setErrorState({ hasError: true, failedDeletions: [] });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle={'Delete folder' + (addPluralTxt ? 's' : '')}
      position="right"
      primaryBtnTxt="Delete"
      secondaryBtnTxt="Cancel"
      givenClass="DeleteDocument"
      primaryBtnColor="BtnRed"
      handleClose={() => togglePanelOpen(false)}
      handlePrimaryBtn={deleteFolder}
      handleSecondaryBtn={() => togglePanelOpen(false)}
      width={560}
      isSubmitting={submitting}
      noOverflowToggle={true}
    >
      <div className="DeleteModal">
        <h4>Permanently Delete</h4>

        <p>
          {`Are you sure you want to permanently delete ${
            addPluralTxt ? 'these folders' : 'this folder'
          }?`}
        </p>
        <div className="LineSpacer3"></div>
        <p className="BoldText">
          {`You'll permanently lose all information about
            ${addPluralTxt ? 'these folders:' : 'this folder:'}`}
        </p>
        <div className="DocDeleteList">
          {selectedData?.map((item, index) => (
            <div className="DLItem" key={index + item.folders__id}>
              -{item.list__itemname}
            </div>
          ))}
        </div>
        {loading && !data ? (
          <Spinner positionAbsolute />
        ) : data?.details.foldersAndDocumentsToDelete &&
          data?.details.foldersAndDocumentsToDelete.length > 0 ? (
          <>
            <p className="BoldText">
              There contents will also be permanently deleted:
            </p>
            <div className="DocDeleteList">
              {data?.details.foldersAndDocumentsToDelete.map((d) => (
                <div className="DLItem" key={d.folderAndDocument}>
                  -{d.folderAndDocument}
                </div>
              ))}
            </div>
          </>
        ) : null}
        <div className="DeleteModalWarning">
          <img src={warningIcon} alt="warning" />
          <div style={{ alignContent: 'center' }}>
            <p>This action cannot be undone</p>
          </div>
        </div>
        {errorState?.hasError && (
          <div className="ErrorTxt">
            {errorState.failedDeletions.length > 0
              ? `Error deleting the following folders:`
              : `Error deleting the following folder:`}
            <ul>
              {errorState.failedDeletions.map((d, i) => (
                <li key={i}>
                  {d.id}: {d.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PopupOrPanel>
  );
};
